/* eslint-disable react/jsx-one-expression-per-line */
import { MedicalCategoryPropertyContext } from 'contexts/MedicalCategoryPropertyContext';
import { MedicalServiceContext } from 'contexts/MedicalServiceContext';
import { MedicalCategory, MedicalCategoryProperty, MedicalCuration, MedicalService } from 'model/graphql';
import React, { SetStateAction, useContext, useEffect, useMemo, useState, Dispatch, useCallback } from 'react';
import { IdNameWise } from 'utils/types';
import { LocationContext } from 'contexts/LocationContext';
import { CategoryContext } from 'contexts/CategoryContext';
import { Checkbox, FormControlLabel, Button as MuiButton } from '@material-ui/core';
import SortingIcon from 'assets/icons/ic_sorting.svg?url';
import speechIcon from 'assets/icons/ic_speech.svg?url';
import InformationIcon from 'assets/icons/ic_information.svg?url';
import LocationMediumIcon from 'assets/icons/ic_location_medium';
import { getFormattedNumber } from 'utils/format';
import { useRouter } from 'next/router';

import {
  URL_QUERY_STRING_NAME_FILTER,
  URL_QUERY_STRING_NAME_LEVEL_1,
  URL_QUERY_STRING_NAME_LEVEL_2,
  URL_QUERY_STRING_NAME_LEVEL_3,
  URL_QUERY_STRING_NAME_LATITUDE,
  URL_QUERY_STRING_NAME_LONGITUDE,
  URL_QUERY_STRING_NAME_TARGET_CATEGORY,
  URL_QUERY_STRING_NAME_PARTNER_ONLY,
  LOCAL_STORAGE_KEY_USER_SETTING_COORDINATE,
  MINIMUM_VERSION_CHAT_BOT,
  FAMILY_PARENTS_CURATION_ID,
  FAMILY_CHILDREN_CURATION_ID,
} from 'types/constants';
import { SortingOrder, SortingOrderLabels, SortingWithDistance } from 'pages/medical-service/types';

import FixSwipeable from 'components/atoms/FixSwipeable';
import { Coordinate } from 'model';
import Text from 'components/Typography/Text';
import { AnalyticsContext, TrackingEventName } from 'contexts/AnalyticsContext';
import { calcDiscountRate } from 'utils/calculate';
import Button from 'components/Forms/Button';
import { ButtonSize, ButtonStyleType } from 'components/Forms/Button/types';
import useUserAgent from 'hooks/useUserAgent';
import { UserContext } from 'contexts/UserContext';
import useNavigation, { DeepLinkName } from 'hooks/useNavigation';
import Color from 'styles/colors';
import { validCoordinateInKorea } from 'utils/validation';
import { useToastContext } from 'contexts/ToastContext';
import MedicalServiceList from '../MedicalServiceList';
import MedicalFilterView from '../MedicalFilterView';
import * as styles from './styles';
import SortingDrawer from '../SortingDrawer';

const dummyAllItem: IdNameWise = {
  id: null,
  name: '전체',
};

interface MedicalServiceViewProps {
  curation?: MedicalCuration;
  category: MedicalCategory;
  isVisible: boolean;
  allowFetch: boolean;
  isInitialRouterUsed: boolean;
  setInitialRouterUsed: Dispatch<SetStateAction<boolean>>;
  onSubcategoryUpdate?(category: MedicalCategory): void;
}

interface ScrollableSelectProps<T extends IdNameWise> {
  items: T[];
  onChanged: (item: T | null) => void;
  selectedItem: T;
}

function serializeChildCategoryIds(categories: MedicalCategory[], results: string[] = []) {
  for (const category of categories) {
    results.push(category?.id);
    if (category.children) {
      serializeChildCategoryIds(category.children, results);
    }
  }
}
const distanceFromAddress = Object.freeze({ 서울특별시: 10 }); // unit: km
const distanceAtStep = [5, 10, 30, 100, 500];

function MedicalServiceView(props: MedicalServiceViewProps) {
  const {
    curation,
    category,
    isVisible,
    allowFetch,
    isInitialRouterUsed,
    setInitialRouterUsed,
    onSubcategoryUpdate,
  } = props;

  const router = useRouter();
  const { address, coordinate, setCoordinate } = useContext(LocationContext);
  const { categoryDictionary } = useContext(CategoryContext);
  const {
    medicalServices,
    fetchMedicalServices,
    fetchMoreMedicalServices,
    isLoading,
    isInitialDataFetched,
    isEndOfTheList,
    totalCount,
  } = useContext(MedicalServiceContext);
  const { medicalCategoryPropertyDictionary, fetchMedicalCategoryProperties, setOpenLocationPickerModal } = useContext(
    MedicalCategoryPropertyContext
  );
  const { trackEvent } = useContext(AnalyticsContext);
  const { pushDeeplink } = useNavigation();
  const { isBrowser, checkMiniumAppVersion } = useUserAgent();
  const { showInfo } = useToastContext();

  const [selectedSubcategory, setSelectedSubcategory] = useState<MedicalCategory>(null);
  const [selectedSubSubcategory, setSelectedSubSubcategory] = useState<MedicalCategory>(null);
  const [targetCategory, setTargetCategory] = useState<MedicalCategory>(null);

  const [categoryProperties, setCategoryProperties] = useState<MedicalCategoryProperty[]>(null);
  const [selectedPropertyValueIds, setSelectedPropertyValueIds] = useState<string[]>([]);
  const [isPartnerOnly, setPartnerOnly] = useState<boolean>(false);
  const [selectedSortingWithDistance, setSelectedSortingWithDistance] = useState<SortingWithDistance>({
    sorting: SortingOrder.clinicScoreDesc,
  });
  const [selectedDistanceStep, setSelectedDistanceStep] = useState<number>();
  const [isSortingBottomSheetOpen, setSortingBottomSheetOpen] = useState<boolean>(false);
  const { isSignedIn } = useContext(UserContext);

  const { children: subcategories } = category;

  const addressText = useMemo(() => {
    const userSettingCoordinate = localStorage.getItem(LOCAL_STORAGE_KEY_USER_SETTING_COORDINATE);
    if (!userSettingCoordinate) {
      return '위치 선택하기';
    }
    return [address?.sigungu, address?.dongmyun].join(' ') || '알 수 없는 위치';
  }, [address]);

  const invalidCoordinate = useCallback(() => {
    showInfo('위치를 찾을 수 없습니다. 마지막 설정위치로 설정합니다.');
  }, []);

  useEffect(() => {
    const {
      lv1: level1,
      lv2: level2,
      lv3: level3,
      f: filter,
      lt: latitude,
      lg: longitude,
      po: partnerOnly,
    } = router.query;

    if (partnerOnly) {
      setPartnerOnly(partnerOnly === '1');
    }

    if (latitude && longitude) {
      const coordinate: Coordinate = { latitude: Number(latitude), longitude: Number(longitude) };

      if (validCoordinateInKorea(coordinate)) {
        setCoordinate(coordinate);
      } else {
        invalidCoordinate();
      }
    } else if (latitude || longitude) {
      invalidCoordinate();
    }

    //  카테고리 정보는 init이 필요하고 나머지 설정은 라우터에 따라서 바뀌기 때문에
    if (isInitialRouterUsed || category.id !== level1) {
      if (!level1) setInitialRouterUsed(true);
      return;
    }

    let foundSubcategory: MedicalCategory;
    if (level2) {
      foundSubcategory = subcategories.find((subcategory) => subcategory.id === level2);
      setSelectedSubcategory(foundSubcategory);
    }

    if (level3) {
      const foundSubSubcategory = foundSubcategory?.children?.find((subcategory) => subcategory.id === level3);
      setSelectedSubSubcategory(foundSubSubcategory);
    }

    if (filter) {
      setSelectedPropertyValueIds(Array.isArray(filter) ? filter : [filter]);
    }

    setInitialRouterUsed(true);
  }, [subcategories]);

  useEffect(() => {
    if (!isInitialRouterUsed || !isVisible) return;

    const handleRouterQuery = (name: string, value: string | string[]) => {
      if (value) {
        router.query[name] = value;
      } else {
        delete router.query[name];
      }
    };

    handleRouterQuery(URL_QUERY_STRING_NAME_LEVEL_1, category?.id);
    handleRouterQuery(URL_QUERY_STRING_NAME_LEVEL_2, selectedSubcategory?.id);
    handleRouterQuery(URL_QUERY_STRING_NAME_LEVEL_3, selectedSubSubcategory?.id);
    handleRouterQuery(URL_QUERY_STRING_NAME_TARGET_CATEGORY, targetCategory?.id);
    handleRouterQuery(URL_QUERY_STRING_NAME_FILTER, selectedPropertyValueIds);
    handleRouterQuery(URL_QUERY_STRING_NAME_PARTNER_ONLY, isPartnerOnly ? '1' : null);
    handleRouterQuery(URL_QUERY_STRING_NAME_LATITUDE, coordinate?.latitude.toString());
    handleRouterQuery(URL_QUERY_STRING_NAME_LONGITUDE, coordinate?.longitude.toString());

    router.replace(
      {
        pathname: router.pathname,
        query: router.query,
      },
      null,
      { shallow: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isVisible,
    category,
    selectedSubcategory,
    selectedSubSubcategory,
    targetCategory,
    selectedPropertyValueIds,
    isPartnerOnly,
    coordinate,
  ]);

  const setProperTargetCategory = (category: MedicalCategory) => {
    // targetCategory의 자식 카테고리가 한 개인 경우 View에서 보여주지 않기 때문에 선택할 수가 없다.
    // 그래서 이 경우 그 한 개 카테고리가 필터를 가지고 있는 경우 필터를 보여줄 수 없는 상황이 되기 때문에 기본으로 자식 카테고리를 선택 한다.
    if (category.children?.length === 1) {
      setTargetCategory(category.children[0]);
    } else {
      setTargetCategory(category);
    }
  };

  useEffect(() => {
    if (selectedSubcategory === null || !selectedSubcategory?.id) {
      // 전체 (parent category로 fetching)
      setProperTargetCategory(category);
    } else if (!selectedSubcategory?.children) {
      // 자식이 없으면 자신 selectedSubcategory fetching, 자식이 있으면 자식에게 위임
      setProperTargetCategory(selectedSubcategory);
    }
  }, [selectedSubcategory]);

  useEffect(() => {
    if (!selectedSubSubcategory) return;

    if (selectedSubSubcategory.id === null) {
      // 전체 (subcategory category로 fetching)
      setProperTargetCategory(selectedSubcategory);
    } else {
      setProperTargetCategory(selectedSubSubcategory);
    }
  }, [selectedSubSubcategory]);

  useEffect(() => {
    setCategoryProperties(null);

    if (!targetCategory) {
      return;
    }

    setPrioritizedCategoryProperties(targetCategory.id);
  }, [medicalCategoryPropertyDictionary, targetCategory]);

  useEffect(() => {
    if (!targetCategory || !allowFetch) return;

    fetchMedicalServices({
      id: targetCategory.id,
      categoryPropertyValueIds: selectedPropertyValueIds,
      latitude: address?.coordinate?.latitude || coordinate.latitude,
      longitude: address?.coordinate?.longitude || coordinate.longitude,
      partnerOnly: isPartnerOnly,
      sortBy: selectedSortingWithDistance.sorting,
      maxDistance: selectedSortingWithDistance.distance || (distanceFromAddress[address?.sido] ?? 30), // default 30 km
    });
  }, [targetCategory, selectedPropertyValueIds, address, selectedSortingWithDistance, isPartnerOnly, isVisible]);

  const level2Category = useMemo(() => (router.query.lv2 ? selectedSubcategory : dummyAllItem), [
    router.query.lv2,
    selectedSubcategory,
  ]);

  useEffect(() => {
    if (isVisible && onSubcategoryUpdate && level2Category) {
      onSubcategoryUpdate(level2Category);
    }
  }, [isVisible, level2Category, onSubcategoryUpdate]);

  function setPrioritizedCategoryProperties(categoryId) {
    // targetCategory가 3 depth category인데 부모 카테고리의 secondDepthFilter가 true이면 2 depth(부모) category property를 사용함

    if (targetCategory.depth === 3) {
      const parentCategory = categoryDictionary[targetCategory.parentId.toString()];
      if (parentCategory.secondDepthFilter) {
        if (!fetchMedicalCategoryProperties({ id: parentCategory.id })) {
          // 이미 fetch해 온 category가 있는 경우 fetchMedicalCategoryProperty가 false를 반환함.
          setCategoryProperties(medicalCategoryPropertyDictionary[parentCategory.id]);
        }
        return;
      }
    }

    if (!fetchMedicalCategoryProperties({ id: categoryId })) {
      // 이미 fetch해 온 category가 있는 경우 fetchMedicalCategoryProperty가 false를 반환함.
      setCategoryProperties(medicalCategoryPropertyDictionary[categoryId]);
    }
  }

  const familyCategoryIds = useMemo<string[]>(() => {
    if (!targetCategory) return [];

    const allChildCategories: string[] = targetCategory?.parentId ? [targetCategory.parentId.toString()] : [];
    serializeChildCategoryIds([targetCategory], allChildCategories);
    return allChildCategories;
  }, [targetCategory]);

  const hasMultipleSubcategories = useMemo(
    () => subcategories?.length > 1 || (subcategories?.length === 1 && subcategories[0].children?.length > 1),
    [subcategories]
  );
  const hasMultipleSubSubcategories = useMemo(() => selectedSubcategory?.children?.length > 1, [selectedSubcategory]);
  const hasCategoryProperties = useMemo(() => categoryProperties?.length > 0, [categoryProperties]);

  const onClickCardAction = useCallback(
    (medicalService: MedicalService, index?: number) => {
      const discountRate = calcDiscountRate(medicalService.price, medicalService.originalPrice);

      trackEvent('clinic_productList_product_click', {
        productId: medicalService.clinicEventId,
        productName: medicalService.displayName,
        ranking: index + 1,
        hospitalId: medicalService.hospitalId,
        hospitalName: medicalService.medicalServiceSummary.hospitalName,
        isAffiliate: !!medicalService.clinicEventId,
        level1CategoryId: category?.id,
        level1CategoryName: category?.name,
        level2CategoryId: level2Category?.id,
        level2CategoryName: level2Category?.name,
        // TODO array support
        // filter_category_id: '',
        // filter_category_name: '',
        // filter_id: '',
        // filter_name: '',
        price: medicalService.price,
        discountRate: medicalService.marketPrice || discountRate === '0' ? null : discountRate,
        rating: medicalService.medicalServiceSummary.ratingMean,
        reviewCount: medicalService.medicalServiceSummary.reviewsCount,
        orderBy: SortingOrderLabels[selectedSortingWithDistance?.sorting],
      });
    },
    [
      category?.id,
      category?.name,
      curation.id,
      curation.name,
      level2Category?.id,
      level2Category?.name,
      selectedSortingWithDistance?.sorting,
      trackEvent,
    ]
  );

  const onClickBookmarkAction = useCallback(
    (medicalService: MedicalService, isBookmarked: boolean, index: number) => {
      trackEvent('clinic_product_addwishlist_click', {
        action: isBookmarked ? 'cancel' : 'add',
        type: 'plp',
        productId: medicalService.clinicEventId,
        productName: medicalService.displayName,
        ranking: index + 1,
        hospitalId: medicalService.hospitalId,
        hospitalName: medicalService.medicalServiceSummary.hospitalName,
      });
    },
    [trackEvent]
  );

  const handleEmptyList = useCallback(() => {
    if (isVisible) {
      trackEvent('clinic_productList_empty_view', {
        curationId: curation?.id,
        curationName: curation?.name,
        level1CategoryId: category?.id,
        level1CategoryName: category?.name,
        level2CategoryId: level2Category?.id,
        level2CategoryName: level2Category?.name,
        lt: coordinate?.latitude,
        lg: coordinate?.longitude,
        action: isPartnerOnly ? 'check' : 'uncheck',
      });
    }
  }, [
    isVisible,
    curation?.id,
    curation?.name,
    category?.id,
    category?.name,
    level2Category?.id,
    level2Category?.name,
    coordinate?.latitude,
    coordinate?.longitude,
    isPartnerOnly,
  ]);

  // iOS 심사 거절 대응으로 인해 하단 코드 임시로 주석 처리 (챗봇 관련)
  // const isChatbotBannerVisible = useMemo(
  //   () => category.chatBotEnabled && isVisible && !isBrowser && checkMiniumAppVersion(MINIMUM_VERSION_CHAT_BOT),
  //   [category.chatBotEnabled, isVisible, isBrowser]
  // // );

  // const aiChatbotTrackingProps = useMemo(
  //   () => ({
  //     curationId: curation.id,
  //     curationName: curation.name,
  //     categoryId1: category.id,
  //     categoryName1: category.name,
  //     categoryId2: selectedSubcategory?.id,
  //     categoryName2: selectedSubcategory?.name,
  //     categoryId3: selectedSubSubcategory?.id,
  //     categoryName3: selectedSubSubcategory?.name,
  //     loginStatus: isSignedIn,
  //   }),
  //   [curation, category, selectedSubcategory, selectedSubSubcategory, isSignedIn]
  // );

  return (
    <>
      {hasMultipleSubcategories && (
        <FixSwipeable>
          <ScrollableChipSelect<MedicalCategory>
            items={[dummyAllItem, ...(subcategories ?? [])]}
            onChanged={(subcategory) => {
              trackEvent('clinic_productList_categoryTab2_click', {
                curationId: curation?.id,
                curationName: curation?.name,
                level1CategoryId: category?.id,
                level1CategoryName: category?.name,
                level2CategoryId: subcategory.id,
                level2CategoryName: subcategory.name,
              });
              setSelectedSubcategory(subcategory);
            }}
            selectedItem={selectedSubcategory}
          />
        </FixSwipeable>
      )}
      {hasMultipleSubSubcategories && (
        <FixSwipeable>
          <ScrollableTextSelect
            items={[dummyAllItem, ...(selectedSubcategory?.children ?? [])]}
            onChanged={(subSubCategory) => {
              setSelectedSubSubcategory(subSubCategory);
            }}
            selectedItem={selectedSubSubcategory}
          />
        </FixSwipeable>
      )}
      {hasCategoryProperties && isVisible && (
        <MedicalFilterView
          category={category}
          subcategory={level2Category}
          medicalCategoryProperties={categoryProperties}
          selectedPropertyValueIds={selectedPropertyValueIds}
          onChanged={(selectedValueIds) => {
            setSelectedPropertyValueIds(selectedValueIds);
          }}
        />
      )}
      {/* iOS 심사 거절 대응으로 인해 하단 코드 임시로 주석 처리 (챗봇 관련) */}
      {/* {isChatbotBannerVisible && (
        <div className={styles.askingAIBanner(!hasCategoryProperties)}>
          <div>
            <Text.Body2Bold className={styles.askingAIBannerText}>{`“${category.name}”`}</Text.Body2Bold>
            <br />
            <Text.Body2 className={styles.askingAIBannerText}> AI챗봇으로 알아보세요.</Text.Body2>
          </div>
          <Button.WithImage
            className={styles.askingAIBannerCTA}
            styleType={ButtonStyleType.commonFilled}
            size={ButtonSize.small}
            leftImage={<img src={speechIcon} alt="Ask to Goodoc AI" />}
            onClick={() => {
              pushDeeplink(DeepLinkName.chatBot, { query: `${category.name} 시술의 효과와 부작용을 알려주세요.` });
            }}
          >
            질문하기
          </Button.WithImage>
        </div>
      )} */}
      <div className={styles.listHeaderView}>
        <button
          onClick={() => {
            trackEvent('clinic_productList_location_click', { locationText: addressText });
            setOpenLocationPickerModal(true);
          }}
          className={styles.selectLocationButton()}
        >
          <LocationMediumIcon color={Color.Gray['60']} />
          {addressText}
        </button>

        <div className={styles.rightColumn}>
          <FormControlLabel
            className={styles.partnerFormControlLabel}
            control={
              <Checkbox
                color="primary"
                checked={isPartnerOnly}
                size="small"
                onChange={() => {
                  trackEvent('clinic_productList_affiliate_click', { action: isPartnerOnly ? 'uncheck' : 'check' });
                  setPartnerOnly(!isPartnerOnly);
                }}
                name="partnerOnly"
                inputProps={{ 'aria-label': 'Goodoc partners only' }}
                style={{ padding: '9px 3px 9px 9px' }}
              />
            }
            label="굿닥제휴"
          />
          <MuiButton
            className={styles.sortingButton}
            onClick={() => {
              trackEvent('clinic_productList_sorting_click', {
                type: SortingOrderLabels[selectedSortingWithDistance.sorting],
              });
              setSortingBottomSheetOpen(true);
            }}
            endIcon={<img src={SortingIcon} alt="Sort" />}
          >
            {SortingOrderLabels[selectedSortingWithDistance.sorting]}(
            {`${selectedSortingWithDistance.distance || (distanceFromAddress[address?.sido] ?? 30)}km`})
          </MuiButton>
        </div>
      </div>
      <div className={styles.countNumberRow()}>
        <Text.Body3>총 {getFormattedNumber(totalCount)} 개의 결과가 있습니다.</Text.Body3>
      </div>
      <div className={styles.infoRow()}>
        <img className={styles.informationIcon()} src={InformationIcon} alt="info icon" />
        <Text.Body4>굿닥은 정보 제공의 주체가 아니며, 병원 홈페이지로 연결될 수 있습니다.</Text.Body4>
      </div>
      <MedicalServiceList
        medicalServices={medicalServices}
        familyCategoryIds={familyCategoryIds}
        depth={targetCategory?.depth}
        needsLoadingMore={!isEndOfTheList}
        needsListHidden={!isVisible}
        isLoading={isLoading}
        isInitialDataFetched={isInitialDataFetched}
        onLoadMoreButtonClicked={() => {
          fetchMoreMedicalServices();
        }}
        onBeforeCardClicked={onClickCardAction}
        onBeforeBookmarkClicked={onClickBookmarkAction}
        onEmpty={handleEmptyList}
        onListImpression={(idx) => {
          trackEvent('clinic_productList_impression', {
            sequence: idx + 1,
          });
        }}
      />
      <SortingDrawer
        open={isSortingBottomSheetOpen}
        distanceAtStep={distanceAtStep}
        selectedDistanceStep={selectedDistanceStep}
        defaultDistance={distanceFromAddress[address?.sido]}
        selectedSortingWithDistance={selectedSortingWithDistance}
        onSliderChangeCommitted={(_event, value) => {
          const step = Array.isArray(value) ? value[0] : value;
          setSelectedDistanceStep(step);
        }}
        onClose={() => {
          setSelectedSortingWithDistance({
            sorting: selectedSortingWithDistance.sorting,
            distance: distanceAtStep[selectedDistanceStep],
          });
          setSortingBottomSheetOpen(false);
        }}
        onSortingChanged={(sortingOrderKey) => {
          trackEvent('clinic_productList_bottomesheet_sorting_change', {
            type: SortingOrderLabels[sortingOrderKey],
          });
          setSelectedSortingWithDistance({
            sorting: sortingOrderKey,
            distance: distanceAtStep[selectedDistanceStep],
          });
          setSortingBottomSheetOpen(false);
        }}
      />
    </>
  );
}

function ScrollableChipSelect<T extends IdNameWise>({ items, onChanged, selectedItem }: ScrollableSelectProps<T>) {
  return (
    <div className={styles.scrollableItemsView}>
      {items?.map((item) => (
        <span
          key={Number(item.id)}
          className={styles.scrollableChipItem(selectedItem?.id === item.id || (!selectedItem && !item.id))}
          onClick={() => {
            onChanged(item);
          }}
        >
          {item.name}
        </span>
      ))}
    </div>
  );
}

function ScrollableTextSelect<T extends IdNameWise>({ items, onChanged, selectedItem }: ScrollableSelectProps<T>) {
  return (
    <div className={styles.scrollableItemsView}>
      {items?.map((item) => (
        <span
          key={Number(item.id)}
          className={styles.scrollableTextItem(selectedItem?.id === item.id || (!selectedItem?.id && !item.id))}
          onClick={() => {
            onChanged(item);
          }}
        />
      ))}
    </div>
  );
}

export default MedicalServiceView;
